<template>
  <section class="two-column-story">
    <div class="two-column-story__container">
      <div
        class="two-column-story__content"
        :class="{ 'two-column-story__content--inverted': inverted }">
        <!-- Text Column -->
        <div class="two-column-story__text-column">
          <!-- Small Logo/Image -->
          <div
            v-if="showLogo"
            class="two-column-story__logo">
            <template v-if="logoSrc">
              <img
                :src="logoSrc"
                :alt="logoAlt"
                class="two-column-story__logo-image" />
            </template>
            <template v-else>
              <img
                :src="RaccontiImage"
                class="two-column-story__logo-image" />
            </template>
          </div>

          <!-- Text Content -->
          <div class="two-column-story__text-content">
            <text-paragraph
              :bold-text="boldText"
              :title="title"
              :description="description"
              :show-title="showTitle"
              :show-ctas="showCtas"
              :primary-cta-text="primaryCtaText"
              :secondary-cta-text="secondaryCtaText"
              :cta-variant="ctaVariant"
              :show-cta-icon="showCtaIcon"
              :primary-cta-to="primaryCtaTo"
              :secondary-cta-to="secondaryCtaTo"
              @primary-cta-click="$emit('primary-cta-click')"
              @secondary-cta-click="$emit('secondary-cta-click')" />
          </div>
        </div>

        <!-- Image Column -->
        <div class="two-column-story__image-column">
          <img
            :src="imageSrc"
            :alt="imageAlt"
            class="two-column-story__main-image" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
  import RaccontiImage from '~/assets/images/racconti.png'

  import TextParagraph from './TextParagraph.vue'

  // Props
  defineProps({
    logoSrc: {
      type: String,
      required: false,
      default: null
    },
    logoAlt: {
      type: String,
      default: 'Logo'
    },
    boldText: {
      type: String
    },
    title: {
      type: String
    },
    description: {
      type: String
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    imageSrc: {
      type: String
    },
    imageAlt: {
      type: String,
      default: 'Content image'
    },
    inverted: {
      type: Boolean,
      default: false
    },
    showCtas: {
      type: Boolean,
      default: true
    },
    primaryCtaText: {
      type: String,
      default: 'Scopri di più'
    },
    secondaryCtaText: {
      type: String,
      default: 'Contattaci'
    },
    primaryCtaTo: {
      type: String
    },
    secondaryCtaTo: {
      type: String
    },
    ctaVariant: {
      type: String,
      default: 'text',
      validator: value => [ 'solid', 'text' ].includes(value)
    },
    showCtaIcon: {
      type: Boolean,
      default: false
    },
    showLogo: {
      type: Boolean,
      default: true
    }
  })

  // Emits
  defineEmits([ 'primary-cta-click', 'secondary-cta-click' ])
</script>

<style lang="scss" scoped>
.two-column-story {
  width: 100%;
  padding: var(--space-xxl) 0; /* vertical padding */

  &__container {
    max-width: var(--grid-max-width);
    margin: 0 auto;
    padding: 0 var(--grid-margin);

    @include mq($until: tablet) {
      padding: 0 var(--space-s);
    }
  }

  &__content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 148px; /* ~148px gap as requested */
    align-items: center;

    @include mq($until: desktop) {
      gap: var(--space-xl);
    }

    @include mq($until: tablet) {
      grid-template-columns: 1fr;
      gap: var(--space-l);
    }

    &--inverted {
      .two-column-story__text-column {
        order: 2;
      }

      .two-column-story__image-column {
        order: 1;
      }

      @include mq($until: tablet) {
        .two-column-story__text-column {
          order: 1;
        }

        .two-column-story__image-column {
          order: 2;
        }
      }
    }
  }

  &__text-column {
    display: flex;
    flex-direction: column;
    gap: var(--space-l);
  }

  &__logo {
    // Logo container
  }

  &__logo-image {
    max-width: 300px;
    height: auto;
    display: block;

    @include mq($until: tablet) {
      max-width: 250px;
    }
  }

  &__text-content {
    // TextParagraph will handle its own styling
    :deep(.text-paragraph__container) {
      padding: 0; /* Remove default padding since we're handling it here */
    }
  }

  &__image-column {
    // Image column container
  }

  &__main-image {
    width: 100%;
    height: auto;
    display: block;

    // border-radius: 8px;
  }
}
</style>
